import * as React from 'react'
import { PortalWithState } from 'react-portal'

import { PortalContext } from 'modules/core/lib/portalContext'

import type { ClosePortalFn } from './useCloseProxy'
import useCloseProxy from './useCloseProxy'
import usePortalConfiguration from './usePortalConfiguration'
// Hooks
import useScroll from './useScroll'

interface ProviderProps {
  closePortal: () => void
  closePortalProxy: (closePortal: () => void) => ClosePortalFn
}

const PortalProviders = ({
  closePortal,
  closePortalProxy,
  children,
}: React.PropsWithChildren<ProviderProps>) => {
  return (
    <PortalContext.Provider
      value={{
        closePortal: closePortalProxy(closePortal),
      }}
    >
      {children}
    </PortalContext.Provider>
  )
}

interface Props {
  locked?: boolean
  onClose?: () => void
}

const Portal = ({
  locked = false,
  children,
  onClose: originalOnClose = () => {},
}: React.PropsWithChildren<Props>) => {
  useScroll()

  const { modalClosed, onClose, closePortalProxy } = useCloseProxy({
    onClose: originalOnClose,
  })

  const portalProps = usePortalConfiguration({
    locked,
    onClose,
  })

  if (modalClosed) return null

  return (
    <PortalWithState
      defaultOpen={portalProps.defaultOpen}
      closeOnOutsideClick={portalProps.closeOnOutsideClick}
      closeOnEsc={portalProps.closeOnEsc}
      onClose={portalProps.onClose}
    >
      {({ closePortal, portal }) => (
        <PortalProviders closePortal={closePortal} closePortalProxy={closePortalProxy}>
          {portal(children)}
        </PortalProviders>
      )}
    </PortalWithState>
  )
}

export default Portal
